/**
 * URLs amigables de cartas.
 *
 * Formato canónico:
 *   /carta/{slug}--{cardId}/{language}[/{variant}][?graded=...]
 *
 * Ejemplo:
 *   /carta/charizard-ex--swsh1-4/es/reverse_holo?graded=PSA%2010
 *
 * El `--` separa la parte decorativa (slug) del `cardId` real, para que el
 * componente CardDetail pueda parsear el ID sin ambigüedades (los cardIds
 * de TCGdex/Riftbound contienen guiones simples internos como "swsh1-4").
 *
 * Se mantienen las rutas `/card/{cardId}/...` en el Router por compatibilidad
 * hacia atrás para links viejos ya compartidos por usuarios.
 */

export function slugify(name) {
  if (!name) return "carta";
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")  // Quita diacríticos: café → cafe
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")       // No alfanuméricos → guion
    .replace(/^-+|-+$/g, "")           // Trim guiones extremos
    .slice(0, 60) || "carta";           // Cap defensivo
}

/**
 * Construye la ruta canónica de una carta.
 * @param {object} p
 * @param {string} p.cardId     Requerido — el id real (ej. "swsh1-4", "ogn-036-298")
 * @param {string} [p.name]     Nombre para el slug decorativo
 * @param {string} [p.language] "es" | "en" | "ja" | "otro"
 * @param {string} [p.variant]  "normal" | "reverse_holo" | "price_pack" | "sealed"
 * @param {string} [p.graded]   Clave graded ("PSA 10", etc.) — se agrega como ?graded=
 * @returns {string} Path relativo listo para `<Link to=…>`
 */
export function cardPath({ cardId, name, language, variant, graded } = {}) {
  if (!cardId) return "/catalog";
  const slug = slugify(name || cardId);
  let url = `/carta/${slug}--${cardId}/${language || "es"}`;
  if (variant && variant !== "normal") url += `/${variant}`;
  if (graded) url += `?graded=${encodeURIComponent(graded)}`;
  return url;
}

/**
 * Extrae el cardId real desde el `:slugId` de la ruta amigable.
 * Si la ruta vieja `/card/:cardId/...` es la que matcheó, esta función no se usa
 * (el cardId ya viene directo en `useParams()`).
 */
export function parseCardIdFromSlug(slugId) {
  if (!slugId) return null;
  const idx = slugId.lastIndexOf("--");
  if (idx === -1) return slugId; // sin slug decorativo — el slugId ES el cardId
  return slugId.slice(idx + 2);
}
